// @flow

import React from 'react';
import get from 'lodash/get';
import {browserHistory} from 'react-router';
import styled from 'styled-components';
import {CSSTransition} from 'react-transition-group';

import type {NotificationType} from 'app/notifly/types';
import {colors} from 'js-libs/sweetcommons/styles';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {getContentType, type contentType} from 'app/utils/content-type';
import {getActivityTitleForNotification} from 'app/activity/shared-utils';
import {capitalizeFirstLetter} from 'app/utils/strings';
import {getPathFromURL} from 'app/utils/urls';
import {getElapsedTime} from 'app/general/dates';

const getNotificationPhrase = (n) =>
  `${capitalizeFirstLetter(
    getActivityTitleForNotification(n)
  )} ${getElapsedTime(n.timestamp)}`;

const getNotificationsText = (notifications) => (
  <div className="small">
    {notifications.length === 1 ? (
      getNotificationPhrase(notifications[0])
    ) : (
      <ul className="text-left pl-3 mb-0">
        {[]
          .concat(notifications)
          .reverse()
          .map((n, index) => (
            <li key={index}>{getNotificationPhrase(n)}</li>
          ))}
      </ul>
    )}
  </div>
);

function goToLastNotificationURL(
  e: SyntheticMouseEvent<any>,
  notifications: Array<NotificationType>
) {
  const actionURL = get(notifications[0], 'action_object.html_url');

  if (actionURL != null) {
    e.stopPropagation();
    e.preventDefault();
    browserHistory.push(getPathFromURL(actionURL));
  }
}

const StyledIconContainerSpan = styled.span`
  position: relative;

  .notifications {
    position: absolute;
    width: 10px;
    height: 10px;
    background: ${colors.lightLinkBlue};
    background: radial-gradient(
      ellipse at center,
      ${colors.lightLinkBlue} 0%,
      ${colors.linkBlue} 100%
    );
    overflow: hidden;
    border-radius: 50%;
    border: 2px solid #fff;

    &.notifications-enter,
    &.notifications-appear {
      opacity: 0.01;
      transform: scale(0.5);
    }

    &.notifications-enter-active,
    &.notifications-appear-active {
      opacity: 1;
      transform: scale(1);
      transition: all 500ms cubic-bezier(0.175, 0.885, 0.32, 1.5);
    }
  }
`;

type Props = {
  contentType: contentType,
  notifications: Array<NotificationType>,
};

export const DocumentIconWithNotifications = ({
  contentType,
  notifications,
}: Props) =>
  notifications.length > 0 ? (
    <LazyTooltip tooltip={getNotificationsText(notifications)}>
      <StyledIconContainerSpan
        onClick={(e) => goToLastNotificationURL(e, notifications)}
      >
        <CSSTransition
          appear={true}
          in={true}
          timeout={500}
          classNames="notifications"
        >
          <span className="notifications" />
        </CSSTransition>
        <DocumentIcon contentType={contentType} className="fa-fw" />
      </StyledIconContainerSpan>
    </LazyTooltip>
  ) : (
    <DocumentIcon contentType={contentType} className="fa-fw" />
  );

DocumentIconWithNotifications.defaultProps = {
  notifications: [],
};

type DocumentIconProps = {|
  contentType: contentType,
  className?: string,
|};

export const DocumentIcon = ({
  contentType,
  className = '',
}: DocumentIconProps) => (
  <i className={`${className} ${getContentType(contentType).icon}`} />
);
